import type {
  PullMapper,
  QualityMetric,
  QualityMetricInternalConfigurations,
} from 'shared/api/quality-metric-rq.service.api';

/**
 * What the Data source section's editor holds while it is open. `key` is the
 * raw input, untrimmed; `enabled` is whatever the switch last produced; and
 * both `''` (a cleared Select) and `null` mean "use the organization's
 * default", which is why neither is narrowed away here.
 */
export interface PullDraft {
  key: string;
  enabled?: boolean;
  mapper?: PullMapper | '' | null;
}

/**
 * Only the pull block is read, so the metric is typed by what is touched
 * rather than by the whole entity — that keeps the helpers usable from a test
 * fixture and says plainly that nothing else on the metric matters here.
 */
interface PullDraftArgs {
  draft: PullDraft;
  metric?: Pick<QualityMetric, 'internalConfigurations'> | null;
}

/**
 * Maps the data-source draft onto the request body. The measure key is the
 * switch: a key means the metric is mapped, no key means it is not.
 *
 * `undefined` means "send no `internalConfigurations` key at all" — PUT merges
 * that object key by key, so omitting it keeps whatever is stored. Clearing has
 * to be explicit, which is why an empty key only emits `{ pull: null }` when
 * there was a configuration to drop.
 *
 * `pull` itself is **replaced whole**, so every field goes on every write: an
 * omitted `enabled` would come back `false` and an omitted `mapper` would reset
 * the metric to the organization default.
 */
export const toInternalConfigurations = ({
  draft,
  metric,
}: PullDraftArgs): QualityMetricInternalConfigurations | undefined => {
  const key = draft.key.trim();
  if (key) return { pull: { key, enabled: !!draft.enabled, mapper: draft.mapper || null } };
  return metric?.internalConfigurations?.pull ? { pull: null } : undefined;
};

/** True when saving would change what the sync service pulls for this metric. */
export const hasPullChange = ({ draft, metric }: PullDraftArgs): boolean => {
  const pull = metric?.internalConfigurations?.pull;
  return (
    draft.key.trim() !== (pull?.key ?? '') ||
    !!draft.enabled !== !!pull?.enabled ||
    (draft.mapper || null) !== (pull?.mapper ?? null)
  );
};

/**
 * The titles of an unmatched provider's metrics, in the order the server gave
 * them. Ids missing from `metrics` (a metric deleted since) are skipped.
 *
 * @param metricIds - `UnmatchedProvider.metricIds`.
 * @param metrics - The metrics the screen already loaded.
 * @returns The titles found.
 */
export const resolveMetricTitles = (
  metricIds: string[],
  metrics: Pick<QualityMetric, 'id' | 'title'>[],
): string[] => {
  const titleById = new Map(metrics.map(({ id, title }) => [id, title]));
  return metricIds.flatMap(id => {
    const title = titleById.get(id);
    return title ? [title] : [];
  });
};
